import { supabase, STORAGE_BUCKETS } from '../config/supabase';
import imageCompression from 'browser-image-compression';

export { STORAGE_BUCKETS };

/**
 * Compress image to target size (300-500KB)
 * @param file - Image file to compress
 * @param maxSizeMB - Maximum size in MB (default: 0.4 = 400KB)
 * @returns Compressed File object
 */
export const compressImage = async (
  file: File,
  maxSizeMB: number = 0.4
): Promise<File> => {
  try {
    const options = {
      maxSizeMB: maxSizeMB, // Target: 400KB (between 300-500KB)
      maxWidthOrHeight: 1920, // Max dimension to maintain quality
      useWebWorker: true, // Use web worker for better performance
      fileType: file.type, // Preserve original file type
      initialQuality: 0.85, // Start with 85% quality
    };

    const compressedFile = await imageCompression(file, options);
    
    // If still too large, compress more aggressively
    if (compressedFile.size > maxSizeMB * 1024 * 1024) {
      const moreCompressedOptions = {
        ...options,
        maxSizeMB: maxSizeMB,
        initialQuality: 0.7, // Lower quality for smaller size
      };
      return await imageCompression(file, moreCompressedOptions);
    }

    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Upload image to Supabase Storage (with automatic compression)
 * @param file - Image file to upload
 * @param bucket - Storage bucket name
 * @param folder - Optional folder path within bucket
 * @param compress - Whether to compress image before upload (default: true)
 * @returns Public URL of uploaded image
 */
export const uploadImageToSupabase = async (
  file: File,
  bucket: string,
  folder?: string,
  compress: boolean = true
): Promise<string> => {
  try {
    // Compress image if enabled
    let fileToUpload = file;
    if (compress) {
      const originalSize = file.size;
      fileToUpload = await compressImage(file);
      const compressedSize = fileToUpload.size;
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
      if (compressedSize < originalSize) {
        console.log(`Image compressed: ${(originalSize / 1024).toFixed(2)}KB → ${(compressedSize / 1024).toFixed(2)}KB (${reduction}% reduction)`);
      }
    }

    // Generate unique filename
    const fileExt = fileToUpload.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = folder ? `${folder}/${fileName}` : fileName;

    // Upload file
    const { error } = await supabase.storage
      .from(bucket)
      .upload(filePath, fileToUpload, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Error uploading image:', error);
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error in uploadImageToSupabase:', error);
    throw error;
  }
};

/**
 * Upload product image to Supabase Storage
 */
export const uploadProductImage = async (file: File): Promise<string> => {
  return uploadImageToSupabase(file, STORAGE_BUCKETS.PRODUCT_IMAGES, 'products');
};

/**
 * Upload banner image to Supabase Storage
 */
export const uploadBannerImage = async (file: File): Promise<string> => {
  return uploadImageToSupabase(file, STORAGE_BUCKETS.BANNER_IMAGES, 'banners');
};

/**
 * Upload hero image to Supabase Storage
 */
export const uploadHeroImage = async (file: File): Promise<string> => {
  return uploadImageToSupabase(file, STORAGE_BUCKETS.HERO_IMAGES, 'hero');
};

/**
 * Delete image from Supabase Storage
 */
export const deleteImageFromSupabase = async (
  imageUrl: string,
  bucket: string
): Promise<void> => {
  try {
    // Extract file path from URL
    const urlParts = imageUrl.split('/');
    const filePath = urlParts.slice(urlParts.indexOf(bucket) + 1).join('/');

    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath]);

    if (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteImageFromSupabase:', error);
    // Don't throw - image might not exist or already deleted
  }
};

/**
 * Convert base64 to File object (for migration purposes)
 */
export const base64ToFile = (base64: string, filename: string): File => {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};
